/**
 * CoupleValidationModal – Strategy analysis setup for couples: explore alternatives, downsize/rent params, emergency fund.
 * Used by Aged Care flow before suggestion engine. Saves to coupleValidation and can trigger onValidationComplete.
 */
import React, { useState, useEffect } from 'react';
import { CurrencyInput } from '../ui/index.tsx';
import StampDutyFields from '../ui/StampDutyFields.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { MIN_EMERGENCY_FUND } from '../../utils/emergencyFund.ts';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { annuityPreferenceOf, type AnnuityAppetite, type AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import { guaranteeWindowWords } from '../strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import { calculateAge } from '../../utils/index.ts';
import {
    StructureFundingInSuggestionsFields,
    type StructureFundingInSuggestions,
} from '../../features/agedCare/StructureFundingInSuggestionsFields.tsx';
import type { CoupleValidationState } from '../../features/agedCare/AgedCareAnalyser.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

// This modal's own working shape for the coupleValidation bag — richer/more specific than the
// caller-side CoupleValidationState (which only names exploreAlternatives/suggestionEngineSetupComplete
// and leaves the rest as `unknown` via its index signature). Cast at the two boundary points
// (seeding local state from the prop, and saving back via setCoupleValidation) rather than fighting
// the two types into one — see AssetFundingAllocator.tsx for the same pattern with calc-engine types.
interface CoupleValidationDraft {
    exploreAlternatives?: boolean;
    downsizeNewHomeValue?: number;
    sellingCosts?: number;
    partnerAnnualRent?: number;
    enablePartnerFutureEvent?: boolean;
    partnerEntryYear?: number;
    partnerFutureRoomPrice?: number;
    originalEntrantAliveAtPartnerEntry?: boolean;
    emergencyFund?: number;
    suggestionEngineSetupComplete?: boolean;
    [key: string]: unknown;
}

interface CoupleValidationModalProps {
    isOpen: boolean;
    onClose: () => void;
    coupleValidation: CoupleValidationState;
    setCoupleValidation: (value: CoupleValidationState) => void;
    factFind: DraftFactFind;
    setFactFind?: (updater: DraftFactFind | ((prev: DraftFactFind) => DraftFactFind)) => void;
    scenarioLivingExpenses?: number;
    onValidationComplete?: () => void;
    defaultPartnerRoomPrice?: number;
}

// Default the downsized-home value to ~70% of the current home — a realistic "downsize but
// stay in the area" assumption that scales to each client (rather than a flat figure).
// Falls back to the user's saved value once they've set one.
const resolveDownsizeNewHomeValue = (coupleValidation: CoupleValidationState, factFind: DraftFactFind): number | undefined => {
    const currentHomeValue = factFind?.home?.value || 0;
    const default70 = currentHomeValue > 0 ? Math.round(currentHomeValue * 0.7) : undefined;
    return (coupleValidation.downsizeNewHomeValue as number | undefined) ?? default70;
};

const defaultFutureFields = (coupleValidation: CoupleValidationState, defaultPartnerRoomPrice: number) => ({
    enablePartnerFutureEvent: (coupleValidation.enablePartnerFutureEvent as boolean | undefined) ?? false,
    partnerEntryYear: (coupleValidation.partnerEntryYear as number | undefined) ?? 3,
    partnerFutureRoomPrice:
        Number(coupleValidation.partnerFutureRoomPrice) > 0
            ? (coupleValidation.partnerFutureRoomPrice as number)
            : defaultPartnerRoomPrice || 500000,
    originalEntrantAliveAtPartnerEntry: (coupleValidation.originalEntrantAliveAtPartnerEntry as boolean | undefined) ?? true,
});

const CoupleValidationModal = ({
    isOpen,
    onClose,
    coupleValidation,
    setCoupleValidation,
    factFind,
    setFactFind,
    scenarioLivingExpenses,
    onValidationComplete,
    defaultPartnerRoomPrice = 0,
}: CoupleValidationModalProps) => {
    const [localValidation, setLocalValidation] = useState<CoupleValidationDraft>(() => ({
        ...(coupleValidation as unknown as CoupleValidationDraft),
        ...defaultFutureFields(coupleValidation, defaultPartnerRoomPrice),
        downsizeNewHomeValue: resolveDownsizeNewHomeValue(coupleValidation, factFind),
    }));
    const [structureFundingInSuggestions, setStructureFundingInSuggestions] = useState<StructureFundingInSuggestions>(
        () => factFind?.structureFundingInSuggestions || 'exclude'
    );
    /*
     * THE ANNUITY WILLINGNESS QUESTION (owner, 24-25 Sep 2026), asked here because aged care has a
     * Strategy Analysis Setup of its own — the modules without one ask it as a gate instead
     * (AnnuityPreferenceGate). Same answer, same client record, asked once.
     */
    const [annuityAppetite, setAnnuityAppetite] = useState<AnnuityAppetite>(() => annuityPreferenceOf(factFind as never).appetite);
    const [annuityResidual, setAnnuityResidual] = useState<AnnuityResidual>(() => annuityPreferenceOf(factFind as never).residual);

    const isCouple = factFind?.adviceFor === 'couple';
    /*
     * The guarantee window in this client's own years, for the residual question. It is worked out
     * once here and not inside the JSX, so the sentence and the buttons cannot drift apart.
     */
    const offerGuaranteed = guaranteedShapeAvailable(factFind as never);
    const guaranteeWindow = guaranteeWindowWords({
        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
        sex: factFind?.client?.sex,
        partnerAge: isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
        partnerSex: factFind?.partner?.sex,
        ownerName: factFind?.client?.firstName,
        partnerName: factFind?.partner?.firstName,
    });
    const hasStructures = (factFind?.structures || []).length > 0;
    const hasHome = (factFind?.home?.value || 0) > 0;
    const showCoupleSetup = isCouple && hasHome;
    /** Both of the couple entering care: the home is sold, not downsized, and there is nobody to house. */
    const bothInCare = isCouple && factFind?.goals?.aged_care_for === 'both';
    const partnerFirst = (factFind?.partner?.firstName || '').trim();
    const clientFirst = (factFind?.client?.firstName || '').trim();
    const partnerName = partnerFirst || 'your partner';
    const clientName = clientFirst || 'the person entering care first';

    useEffect(() => {
        if (!isOpen) return;
        setLocalValidation({
            ...(coupleValidation as unknown as CoupleValidationDraft),
            ...defaultFutureFields(coupleValidation, defaultPartnerRoomPrice),
            downsizeNewHomeValue: resolveDownsizeNewHomeValue(coupleValidation, factFind),
        });
        setStructureFundingInSuggestions(factFind?.structureFundingInSuggestions || 'exclude');
        setAnnuityAppetite(annuityPreferenceOf(factFind as never).appetite);
        setAnnuityResidual(annuityPreferenceOf(factFind as never).residual);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, coupleValidation, defaultPartnerRoomPrice, factFind?.structureFundingInSuggestions, factFind?.home?.value]);

    const handleSave = () => {
        const toSave: CoupleValidationDraft = {
            ...localValidation,
            suggestionEngineSetupComplete: true,
        };
        if (toSave.enablePartnerFutureEvent && (!toSave.partnerFutureRoomPrice || Number(toSave.partnerFutureRoomPrice) <= 0)) {
            toSave.partnerFutureRoomPrice = defaultPartnerRoomPrice > 0 ? defaultPartnerRoomPrice : 500000;
        }
        if (setFactFind) {
            setFactFind((prev) => ({
                ...prev,
                ...(hasStructures ? { structureFundingInSuggestions } : {}),
                goals: { ...(prev.goals || {}), annuityAppetite, annuityResidual },
            }));
        }
        setCoupleValidation(toSave as unknown as CoupleValidationState);
        onClose();
        // Automatically proceed to suggestion engine after validation
        if (onValidationComplete) {
            setTimeout(() => {
                onValidationComplete();
            }, 100);
        }
    };

    const handleCancel = () => {
        setLocalValidation({
            ...(coupleValidation as unknown as CoupleValidationDraft),
            ...defaultFutureFields(coupleValidation, defaultPartnerRoomPrice),
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
                {/* Header with app's gradient */}
                <div className="bg-gradient-to-r from-blue-500 to-teal-400 text-white p-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-10"></div>
                    <div className="relative z-10">
                        <div className="flex justify-between items-start">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold mb-1">Strategy Analysis Setup</h2>
                                    <p className="text-blue-100">Let's explore the best funding options for your situation</p>
                                </div>
                            </div>
                            <button 
                                onClick={handleCancel}
                                className="text-white hover:text-blue-200 text-2xl leading-none transition-colors p-1 hover:bg-white hover:bg-opacity-10 rounded-full"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(85vh-180px)]">
                    <div className="space-y-6">
                        {/* Current Preference Display — couples with a home */}
                        {showCoupleSetup && (
                        <>
                        <div className="bg-white border border-blue-200 rounded-xl p-5">
                            <div className="flex items-start gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Your Current Preference</h3>
                                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                                        <div className="flex items-center justify-between">
                                            <span className="text-blue-700 font-medium">
                                                {factFind.partner?.livingArrangement === 'remainHome' && 'Partner remains in family home'}
                                                {factFind.partner?.livingArrangement === 'downsize' && 'Sell home & downsize'}
                                                {factFind.partner?.livingArrangement === 'rent' && 'Sell home & rent elsewhere'}
                                                {!factFind.partner?.livingArrangement && 'Partner remains in family home'}
                                            </span>
                                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                                                ✓ Confirmed
                                            </span>
                                        </div>
                                    </div>
                                    <p className="text-blue-600 text-sm mt-2">This preference will always be included in our analysis</p>
                                </div>
                            </div>
                        </div>

                        {/* Main Question */}
                        <div className="bg-white border border-blue-200 rounded-xl p-5">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-blue-800 mb-2">Would you like us to explore additional options?</h3>
                                    <p className="text-blue-700 leading-relaxed">
                                        We'll always model your preferred arrangement, but we can also explore alternative scenarios to see if they might provide better financial outcomes. This gives you a complete picture of all possibilities.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                    localValidation.exploreAlternatives === false
                                        ? 'border-blue-500 bg-blue-50 shadow-md'
                                        : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                }`}>
                                    <input
                                        type="radio"
                                        name="exploreAlternatives"
                                        value="no"
                                        checked={localValidation.exploreAlternatives === false}
                                        onChange={() => setLocalValidation(prev => ({ ...prev, exploreAlternatives: false }))}
                                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                                    />
                                    <div className="flex-1">
                                        <div className="font-semibold text-gray-800 mb-1">Focus on my preference only</div>
                                        <div className="text-gray-600 text-sm">Model only your stated arrangement to keep the analysis focused</div>
                                    </div>
                                </label>

                                <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                    localValidation.exploreAlternatives === true
                                        ? 'border-blue-500 bg-blue-50 shadow-md'
                                        : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                }`}>
                                    <input
                                        type="radio"
                                        name="exploreAlternatives"
                                        value="yes"
                                        checked={localValidation.exploreAlternatives === true}
                                        onChange={() => setLocalValidation(prev => ({ ...prev, exploreAlternatives: true }))}
                                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                                    />
                                    <div className="flex-1">
                                        <div className="font-semibold text-gray-800 mb-1">Explore all possibilities</div>
                                        <div className="text-gray-600 text-sm">Include your preference plus alternative scenarios for comprehensive analysis</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Alternative scenario parameters - only show if "Yes" is selected */}
                        {localValidation.exploreAlternatives === true && (
                            <div className="bg-white border border-blue-200 rounded-xl p-5">
                                <div className="flex items-start gap-3 mb-4">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        {/*
                                          * SAY WHAT THE FIGURES BUY (owner, 23 Sep 2026: "should we have a small
                                          * intro"). Three boxes asked for numbers without saying what they were for,
                                          * under a heading in the engine's language rather than the client's. Named
                                          * here instead: who is affected, the three things actually modelled, and
                                          * that estimates are good enough — so nobody stalls on a figure that only
                                          * has to be close.
                                          */}
                                        <h3 className="text-lg font-semibold text-blue-800 mb-1">What happens to the family home</h3>
                                        <p className="text-blue-700 text-sm">
                                            {bothInCare
                                                ? <>With {clientName} and {partnerName} both in care, nobody is left living in the family home. We model keeping it, letting it out, and selling it &mdash; the selling costs below price the sale. Rough numbers are fine.</>
                                                : <>When {clientName} moves into care, {partnerName} stays in the family home &mdash; unless it is
                                            sold or let out. We model all three; these figures price the last two. Rough numbers are fine.</>}
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Both in care: there is no new home to price and nobody to house (7 Oct 2026). */}
                                    {!bothInCare && (
                                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                                        <CurrencyInput
                                            label="New home value (if downsizing)"
                                            value={localValidation.downsizeNewHomeValue ?? ''}
                                            onChange={(v) => setLocalValidation(prev => ({ ...prev, downsizeNewHomeValue: v ?? 0 }))}
                                            placeholder="Estimated downsized home value"
                                            className=""
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Defaults to ~70% of your current home value — adjust as needed</p>
                                    </div>
                                    )}
                                    {/* Owner, 23 Sep 2026: the duty on the new home, estimated from the state's own
                                        scale and overwritable — one less thing to work out, never a black box. */}
                                    {!bothInCare && (
                                    <StampDutyFields
                                        state={typeof localValidation.newHomeState === 'string' ? localValidation.newHomeState : ''}
                                        onStateChange={(v) => setLocalValidation(prev => ({ ...prev, newHomeState: v }))}
                                        newHomePrice={localValidation.downsizeNewHomeValue}
                                        duty={typeof localValidation.stampDuty === 'number' ? localValidation.stampDuty : ''}
                                        onDutyChange={(v) => setLocalValidation(prev => ({ ...prev, stampDuty: v }))}
                                        fieldClassName="bg-white rounded-lg p-3 border border-blue-200"
                                    />
                                    )}
                                    
                                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                                        <CurrencyInput
                                            label="Selling costs ($)"
                                            value={localValidation.sellingCosts ?? ''}
                                            onChange={(v) => setLocalValidation(prev => ({ ...prev, sellingCosts: v }))}
                                            placeholder="e.g. 75000"
                                            className=""
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Estimated selling costs in dollars</p>
                                    </div>
                                    
                                    {!bothInCare && (
                                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                                        <CurrencyInput
                                            label={`Annual rent cost (if ${partnerName} rents elsewhere)`}
                                            value={localValidation.partnerAnnualRent ?? ''}
                                            onChange={(v) => setLocalValidation(prev => ({ ...prev, partnerAnnualRent: v ?? 0 }))}
                                            placeholder="20000"
                                            className=""
                                        />
                                    </div>
                                    )}
                                </div>
                            </div>
                        )}
                        </>
                        )}

                        {/* Entity assets in automatic suggestions (structures captured in Client Profile Step 5) */}
                        {hasStructures && (
                            <StructureFundingInSuggestionsFields
                                value={structureFundingInSuggestions}
                                onChange={setStructureFundingInSuggestions}
                            />
                        )}

                        {/* Future life events (optional) — partner may enter residential care later */}
                        {/* With both entering care the second room is modelled on the Aged Care page itself
                            (7 Oct 2026), so this approximation of a later second room is not offered. */}
                        {isCouple && factFind?.goals?.aged_care_for !== 'both' && (
                            <div className="bg-white border border-violet-200 rounded-xl p-5">
                                <div className="flex items-start justify-between gap-3 mb-4">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-violet-100 rounded-lg">
                                            <svg className="w-5 h-5 text-violet-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-violet-950 mb-1">Future life events (optional)</h3>
                                            <p className="text-violet-900/90 text-sm">
                                                If {partnerName} may move into residential aged care later, you can model approximate timing and a second room price. This adds a conditional scenario in the Suggestion Engine — it does not replace your baseline analysis.
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setLocalValidation((prev) => ({
                                                ...prev,
                                                enablePartnerFutureEvent: !prev.enablePartnerFutureEvent,
                                            }))
                                        }
                                        className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                            localValidation.enablePartnerFutureEvent
                                                ? 'bg-violet-700 text-white hover:bg-violet-800'
                                                : 'bg-white text-violet-900 border border-violet-300 hover:bg-violet-50'
                                        }`}
                                    >
                                        {localValidation.enablePartnerFutureEvent ? 'Enabled' : 'Disabled'}
                                    </button>
                                </div>

                                {localValidation.enablePartnerFutureEvent && (
                                    <div className="space-y-4">
                                        <div className="bg-white rounded-lg p-3 border border-violet-100">
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-start">
                                                <div className="flex min-w-0 flex-col">
                                                    <label className="mb-2 block text-sm font-medium text-gray-700">
                                                        Approximate year {partnerName} enters care
                                                    </label>
                                                    <p className="mb-2 min-h-[2.5rem] text-xs leading-snug text-gray-500">
                                                        Projection year from today (1 = first year; aligns with your projection horizon).
                                                    </p>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={10}
                                                        value={localValidation.partnerEntryYear ?? 3}
                                                        onChange={(e) =>
                                                            setLocalValidation((prev) => ({
                                                                ...prev,
                                                                partnerEntryYear: Math.max(1, parseInt(e.target.value, 10) || 1),
                                                            }))
                                                        }
                                                        className="w-full rounded-lg border border-gray-300 px-3 py-2"
                                                    />
                                                </div>
                                                <div className="flex min-w-0 flex-col">
                                                    <label className="mb-2 block text-sm font-medium text-gray-700">
                                                        Second room price for {partnerName} ($)
                                                    </label>
                                                    <p className="mb-2 min-h-[2.5rem] text-xs leading-snug text-gray-500">
                                                        Defaults to the current facility accommodation price if left blank when saving.
                                                    </p>
                                                    <CurrencyInput
                                                        label=""
                                                        value={localValidation.partnerFutureRoomPrice ?? ''}
                                                        onChange={(v) =>
                                                            setLocalValidation((prev) => ({
                                                                ...prev,
                                                                partnerFutureRoomPrice: v ?? 0,
                                                            }))
                                                        }
                                                        placeholder="e.g. facility accommodation price"
                                                        className=""
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-violet-100">
                                            <span className="block text-sm font-medium text-gray-800 mb-1">
                                                {clientName} at {partnerName}&apos;s entry date
                                            </span>
                                            <p className="text-xs text-gray-600 mb-2">
                                                Affects whether an earlier refundable deposit is assumed available to fund {partnerName}&apos;s room in this illustration.
                                            </p>
                                            <details className="text-xs text-gray-600 group mb-5">
                                                <summary className="cursor-pointer text-violet-700 font-medium list-none flex items-center gap-1 pb-1">
                                                    <span className="underline decoration-violet-300 underline-offset-2 group-open:no-underline">Why we ask</span>
                                                </summary>
                                                <p className="mt-2 mb-1 leading-relaxed text-gray-600 border-l-2 border-violet-200 pl-3">
                                                    A refundable accommodation deposit stays linked to the resident who paid it until their arrangement ends. Here, if {clientName} is still living when {partnerName} enters care, we do not assume that deposit is available for {partnerName}&apos;s admission. If {clientName} has died before {partnerName} enters care, this model assumes any refundable amount still due back may reduce funding needed for {partnerName}&apos;s room. In practice, timing, provider agreements, and estate matters vary — this is a simple illustration only, not a guarantee. Always confirm details with your aged care provider.
                                                </p>
                                            </details>
                                            <div className="mt-2 grid w-full grid-cols-2 gap-3 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setLocalValidation((prev) => ({
                                                            ...prev,
                                                            originalEntrantAliveAtPartnerEntry: true,
                                                        }))
                                                    }
                                                    className={`py-2.5 px-3 rounded-lg border text-sm font-medium text-center min-h-[42px] ${
                                                        localValidation.originalEntrantAliveAtPartnerEntry
                                                            ? 'bg-violet-100 text-violet-900 border-violet-300 shadow-sm'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    Still alive
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setLocalValidation((prev) => ({
                                                            ...prev,
                                                            originalEntrantAliveAtPartnerEntry: false,
                                                        }))
                                                    }
                                                    className={`py-2.5 px-3 rounded-lg border text-sm font-medium text-center min-h-[42px] ${
                                                        !localValidation.originalEntrantAliveAtPartnerEntry
                                                            ? 'bg-violet-100 text-violet-900 border-violet-300 shadow-sm'
                                                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    Has passed
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Emergency Fund Section */}
                        <div className="bg-white border border-green-200 rounded-xl p-5">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-green-800 mb-1">Emergency Fund Protection</h3>
                                    <p className="text-green-700 text-sm">How much do you wish to retain for emergency funds?</p>
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-lg p-4 border border-green-200">
                                <div className="relative mb-3">
                                    <CurrencyInput
                                        label="Emergency Fund Amount ($)"
                                        value={localValidation.emergencyFund ?? ''}
                                        onChange={(v) => setLocalValidation(prev => ({ ...prev, emergencyFund: v ?? 0 }))}
                                        placeholder="50000"
                                        className=""
                                    />
                                </div>
                                
                                {/* Recommended amount calculation */}
                                <div className="rounded-lg border border-green-200 bg-white p-3 mb-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-semibold text-green-800">Recommended guidance:</span>
                                        <span className="text-lg font-bold text-green-700">
                                            {formatCurrency(Math.max(MIN_EMERGENCY_FUND, (scenarioLivingExpenses || 0) / 2))}
                                        </span>
                                    </div>
                                    <p className="text-xs text-green-600">
                                        The greater of <strong>6 months of your adjusted living expenses</strong> ({formatCurrency(scenarioLivingExpenses || 0)} ÷ 2) or <strong>{formatCurrency(MIN_EMERGENCY_FUND)}</strong> — a retiree&apos;s emergencies tend to be lumpy one-off costs. You can keep a higher amount if you prefer a larger buffer.
                                    </p>
                                </div>

                                <button
                                    onClick={() => {
                                        const guidance = Math.max(MIN_EMERGENCY_FUND, (scenarioLivingExpenses || 0) / 2);
                                        setLocalValidation(prev => ({ ...prev, emergencyFund: guidance }));
                                    }}
                                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
                                >
                                    Use this amount
                                </button>
                            </div>
                        </div>

                        {/* Whether an annuity may be offered at all — an INPUT to the run, like the
                            question below it: "no" means no annuity strategy is generated, here or in
                            any other module. Asked in everyday words; a client should not need to know
                            what an annuity is to answer (owner, 25 Sep 2026). */}
                        <div className="bg-white border border-blue-200 rounded-xl p-5">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-blue-800 mb-1">Guaranteed income for life</h3>
                                    <p className="text-blue-700 text-sm">
                                        Some strategies lock part of the money away with an insurer in exchange for a guaranteed income for life.
                                        Centrelink counts only 60% of money used that way, which can lower the care fees and lift the Age Pension.
                                        Setting one up takes a quote from the insurer, directly or through an adviser, and then an application.
                                        <strong>Should we include these options?</strong>
                                    </p>
                                </div>
                            </div>
                            {/*
                              * A GRID, NOT A WRAPPING ROW. On flex-wrap each button was as wide as its
                              * own label, so three answers to one question came out three different
                              * sizes and the longest one looked like the important one. Equal columns
                              * make them read as what they are: alternatives of equal standing.
                              */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Should we include a guaranteed income for life">
                                {([
                                    { id: 'none', label: 'No — keep it all accessible' },
                                    { id: 'yes', label: 'Yes — include these options' },
                                ] as const).map((o) => (
                                    <button
                                        key={o.id}
                                        type="button"
                                        role="radio"
                                        aria-checked={annuityAppetite === o.id}
                                        onClick={() => setAnnuityAppetite(o.id)}
                                        className={`rounded-lg border-2 px-3.5 py-2.5 text-sm font-semibold transition-all ${
                                            annuityAppetite === o.id ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                                        }`}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            {annuityAppetite === 'yes' && (
                                <>
                                    {/*
                                      * THE WINDOW IN THE CLIENT'S OWN YEARS. "The first half of life
                                      * expectancy" is the clause that decides the answer, and it was
                                      * the one a client could not evaluate. `guaranteeWindowWords`
                                      * states the window without stating a life expectancy, which is
                                      * the line the owner drew on 23 September.
                                      */}
                                    <p className="mt-4 mb-2 text-sm text-gray-700">
                                        <strong>Should any of it come back to the family on death?</strong> Guaranteeing more means a lower income.
                                        The full amount only comes back if death comes early{guaranteeWindow ? `, and ${guaranteeWindow}` : ''}; after that
                                        it falls away to nothing.
                                    </p>
                                    {/*
                                      * THE THIRD SHAPE IS OFFERED HERE, where the client qualifies
                                      * (27 Sep 2026). Before this a client who wanted the full amount
                                      * back whenever they died could only pick "as much as the rules
                                      * allow", which is the tapering plain annuity, and the engine gave
                                      * them that. The guaranteed shape is a different product and sold
                                      * only into government-subsidised care, so it appears only for
                                      * someone the fact-find says qualifies.
                                      */}
                                    <div className={`grid grid-cols-1 ${offerGuaranteed ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-2`} role="radiogroup" aria-label="Should any of it come back to the family">
                                        {([
                                            { id: 'returned', label: 'Yes — as much as the rules allow' },
                                            ...(offerGuaranteed ? [{ id: 'guaranteed', label: 'Yes — the full amount, whenever I die' }] : []),
                                            { id: 'nil', label: 'No — the highest income' },
                                            { id: 'both', label: 'Show me whichever works best' },
                                        ] as Array<{ id: AnnuityResidual; label: string }>).map((o) => (
                                            <button
                                                key={o.id}
                                                type="button"
                                                role="radio"
                                                aria-checked={annuityResidual === o.id}
                                                onClick={() => setAnnuityResidual(o.id)}
                                                className={`rounded-lg border-2 px-3.5 py-2.5 text-sm font-semibold transition-all ${
                                                    annuityResidual === o.id ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                                                }`}
                                            >
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                            <p className="text-xs text-gray-500 mt-2">
                                Saved to the client profile and used by every module, so you&apos;ll only be asked once.
                            </p>
                        </div>

                        {/* How freed-up money is invested — asked HERE, in the setup, rather than as
                            an interim screen between "Run" and the results: it is one of the run's
                            inputs, so it belongs beside the others. The standalone gate in the
                            panel remains for flows that never open this modal (a single client with
                            no structures), and suppresses itself once an answer is recorded. */}
                        <div className="bg-white border border-blue-200 rounded-xl p-5">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-blue-800 mb-1">Investing freed-up money</h3>
                                    <p className="text-blue-700 text-sm">
                                        Some strategies free up money — selling or downsizing the home, for example. How would that money be invested?
                                    </p>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-money-profile">Investment approach</label>
                                <select
                                    id="new-money-profile"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={(factFind?.goals?.newMoneyInvestmentProfile as string) || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!setFactFind) return;
                                        setFactFind((prev) => {
                                            const goals = { ...(prev.goals || {}) } as Record<string, unknown>;
                                            if (value) goals.newMoneyInvestmentProfile = value; else delete goals.newMoneyInvestmentProfile;
                                            return { ...prev, goals } as DraftFactFind;
                                        });
                                    }}
                                >
                                    <option value="">Not sure — assume Conservative (most cautious)</option>
                                    {INVESTMENT_PROFILE_IDS.map((id) => (
                                        <option key={id} value={id}>{INVESTMENT_PROFILES[id].label}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-2">
                                    Saved to the client profile, so you&apos;ll only be asked once. Left unanswered, strategies that invest are
                                    priced conservatively and the results say so.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={handleCancel}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-semibold"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-6 py-2 bg-gradient-to-r from-blue-500 to-teal-400 text-white rounded-lg hover:from-blue-600 hover:to-teal-500 transition-all duration-200 font-semibold shadow-md hover:shadow-lg"
                    >
                        Continue to Analysis
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CoupleValidationModal;
